import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowRight } from "lucide-react";

const lineVariants = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: "0%",
    transition: {
      delay: 0.35 + i * 0.14,
      duration: 1.05,
      ease: [0.21, 0.47, 0.32, 0.98],
    },
  }),
};

const fadeUp = {
  hidden: { opacity: 0, y: 18 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { delay: 0.9 + i * 0.08, duration: 0.7, ease: "easeOut" },
  }),
};

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const gridY = useTransform(scrollYProgress, [0, 1], [0, -80]);
  const opacity = useTransform(scrollYProgress, [0, 0.7], [1, 0.15]);

  return (
    <section
      id="top"
      ref={ref}
      className="relative min-h-[100svh] pt-32 pb-24 sm:pt-40 sm:pb-32 overflow-hidden bg-gradient-to-b from-white via-white to-teal-50/30"
      data-testid="hero-section"
    >
      {/* ambient orbs */}
      <div className="vss-orb bg-teal-300/30 h-[420px] w-[420px] -top-32 -left-32" />
      <div className="vss-orb bg-emerald-300/25 h-[520px] w-[520px] -bottom-40 right-[-6rem]" />
      <div className="vss-orb bg-cyan-200/25 h-[360px] w-[360px] top-1/3 left-1/3" />

      {/* parallax grid */}
      <motion.div
        style={{ y: gridY, opacity }}
        className="pointer-events-none absolute inset-0 opacity-[0.35]"
        aria-hidden="true"
      >
        <div className="absolute inset-0 [background-image:linear-gradient(rgba(15,23,42,0.06)_1px,transparent_1px),linear-gradient(90deg,rgba(15,23,42,0.06)_1px,transparent_1px)] [background-size:64px_64px]" />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-white" />
      </motion.div>

      <div className="relative mx-auto max-w-7xl px-6 lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15, duration: 0.7 }}
          className="mb-8 sm:mb-10 flex items-center gap-3"
        >
          <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />
          <span className="font-mono-a text-[11px] uppercase tracking-[0.28em] text-teal-700">
            Technology Talent Across India · Pune + Vadodara
          </span>
        </motion.div>

        <div className="grid grid-cols-12 gap-6">
          <div className="col-span-12 lg:col-span-10">
            <motion.h1
              initial="hidden"
              animate="show"
              className="font-display text-[42px] sm:text-[64px] lg:text-[104px] leading-[0.98] tracking-[-0.03em] font-extrabold text-slate-900"
              data-testid="hero-headline"
            >
              {[
                <>Enterprise Technology <span className="brand-gradient">Talent.</span></>,
                <>Delivered with <span className="font-serif-a text-slate-700 italic font-normal">Speed.</span></>,
                <>Built for <span className="brand-gradient">Scale.</span></>,
              ].map((line, i) => (
                <span
                  key={i}
                  className="block overflow-hidden pb-1"
                >
                  <motion.span
                    custom={i}
                    variants={lineVariants}
                    className="inline-block"
                  >
                    {line}
                  </motion.span>
                </span>
              ))}
            </motion.h1>
          </div>
        </div>

        <div className="mt-10 sm:mt-14 grid grid-cols-12 gap-8 items-end">
          <motion.p
            variants={fadeUp}
            initial="hidden"
            animate="show"
            className="col-span-12 lg:col-span-6 text-lg sm:text-xl text-slate-600 leading-relaxed max-w-[46ch]"
            data-testid="hero-subline"
          >
            Visionary Staffing Solutions is an India-focused IT recruitment firm
            connecting enterprises with technically validated engineering talent
            from <span className="text-teal-700 font-semibold">Pune</span> and{" "}
            <span className="text-teal-700 font-semibold">Vadodara</span>,
            deployed in <span className="text-teal-700 font-semibold">24 to 48 hours</span>.
          </motion.p>

          <motion.div
            variants={fadeUp}
            initial="hidden"
            animate="show"
            custom={1}
            className="col-span-12 lg:col-span-6 flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:justify-end"
          >
            <a
              href="#contact"
              data-testid="hero-cta-primary"
              className="group inline-flex items-center gap-3 rounded-full bg-gradient-to-r from-cyan-500 via-teal-500 to-emerald-500 text-white px-6 py-3.5 font-semibold hover:brightness-110 transition shadow-[0_10px_28px_rgba(20,184,166,0.32)]"
            >
              Request Talent
              <ArrowRight className="h-4 w-4 transition group-hover:translate-x-0.5" />
            </a>
            <a
              href="#services"
              data-testid="hero-cta-secondary"
              className="inline-flex items-center gap-3 rounded-full border border-slate-300 hover:border-teal-500/50 px-6 py-3.5 text-slate-700 hover:text-teal-700 transition bg-white/70 backdrop-blur"
            >
              Explore Capabilities
            </a>
          </motion.div>
        </div>

        {/* Meta strip */}
        <motion.div
          variants={fadeUp}
          initial="hidden"
          animate="show"
          custom={2}
          style={{ y }}
          className="mt-16 sm:mt-24 grid grid-cols-3 gap-6 sm:gap-8 border-t border-slate-200 pt-8"
        >
          {[
            ["Pune", "Delivery Hub · Maharashtra"],
            ["Vadodara", "Delivery Hub · Gujarat"],
            ["24–48h", "First candidate submissions"],
          ].map(([k, v]) => (
            <div key={k} className="flex flex-col gap-1">
              <span className="font-display text-2xl sm:text-3xl text-slate-900 font-bold">{k}</span>
              <span className="text-[10px] sm:text-xs uppercase tracking-[0.22em] text-slate-500 font-mono-a">
                {v}
              </span>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
